import type ExcelJS from "exceljs";

import type {
    Editable,
    OverwriteMediaItem,
    EditableValue,
    OverwriteScopes,
    ArrayEditableValue,
    EditableWithOverwrites,
    CSSSettings
} from "shared-types";

import { EditableType } from "shared-utils/enums/editables";
import { OverwriteScope } from "shared-utils/enums/overwrites";

import type { EditableOverwrite } from "@/types/types";
import { apolloClient } from "@/apollo/index";
import store from "@/store";

import { deepClone, isValidHttpUrl } from "@/utils";
import { ExportableEditableTypes, ExportableTranslationTypes } from "@/enums/editables";
import { ExportGroupScopeLabel } from "@/enums/overwrites";
import * as editableUtils from "@/utils/editables";
import downloadFile from "@/utils/downloadFile";
import mediaItemQuery from "@/apollo/queries/MediaItem.gql";
import { campaignDefaultLanguage, CampaignGetters } from "@/store/modules/campaign";
import { EditorAction, EditorGetters } from "@/store/modules/editor";
import { mimetype } from "@/enums/mimetype";
import excelAddress from "@/utils/excelAddress";
import { BRAND_LOGO_BASE64, LOGO_POSITION } from "@/services/excel/brandLogo";
import loadExcelJs from "@/services/excel/loadExcelJs";
import {
    applyStyle,
    labelStyle,
    languageGroupNameStyle,
    setHeaderRowStyle,
    setInfoRowStyle,
    silkaFont,
    valueStyle
} from "@/services/excel/theme";

export type OverwriteWithEditable = {
    editable: Editable;
    scope: OverwriteScopes;
    editableGroupValueIds: string[];
    masterTemplateId?: string | null;
    language: string;
    value: EditableValue | null | undefined;
    mediaItem: OverwriteMediaItem | null;
    settings?: CSSSettings | null;
    defaultValue?: EditableValue | null;
    modified?: number;
    __typename?: "Overwrite";
};

declare module "exceljs" {
    interface Worksheet {
        dataValidations: {
            model: Record<string, DataValidation>;
        };
    }
}

// https://stackoverflow.com/questions/3809401/what-is-a-good-regular-expression-to-match-a-url
const urlRegex = /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/;

interface DataValidation {
    allowBlank: boolean;
    error: string;
    errorTitle: string;
    formulae: string[];
    showErrorMessage: boolean;
    type: "list";
}

type ExportType = "global" | "template";
type OverwriteScopeType = (typeof OverwriteScope)[keyof typeof OverwriteScope];

interface WorksheetConfig {
    masterTemplateLabelMap: Record<string, string[]>;
    masterTemplateLabels: string[];
    masterTemplateIds: string[];
    exportType: ExportType;
}

interface LanguageUpdateConfig {
    editableGroups: Record<string, { editables: string[]; editableGroupValues: string[] }>;
    editables: Record<string, Editable>;
    masterTemplateIds: string[];
    language: string;
    languageMapping: Record<string, string>;
    normalizedLanguageMapping: Record<string, string>;
    canSeeRestrictedEditables: boolean;
}

const addInfoRow = (
    sheet: ExcelJS.Worksheet,
    fixedColumns: string[],
    headersColumns: { name: string }[],
    hogarthLogo: number,
    title: string,
    subtitle: string
) => {
    sheet.columns = [
        ...fixedColumns.map(name => ({ key: name, width: 40, style: labelStyle })),
        ...headersColumns.map(header => ({
            key: header.name,
            width: 40,
            style: valueStyle
        }))
    ];

    sheet.views = [{ state: "frozen", xSplit: 1, ySplit: 2 }];
    sheet.addRow(["", ""]);
    setInfoRowStyle(sheet, 1);
    sheet.mergeCells(1, 2, 1, headersColumns.length + 1);
    sheet.getCell(1, 2).value = {
        richText: [
            {
                font: {
                    ...silkaFont,
                    bold: true,
                    size: 20
                },
                text: `${title} \r\n`
            },
            {
                font: {
                    ...silkaFont
                },
                text: subtitle
            }
        ]
    };

    sheet.addImage(hogarthLogo, LOGO_POSITION);
};

const addValuesAndFilters = (
    columnValues: string[],
    valuesSheet: ExcelJS.Worksheet,
    inputSheet: ExcelJS.Worksheet,
    valueColumnIndex: number,
    inputColumnIndex: number
) => {
    columnValues.forEach((valueName: string, valueIndex: number) => {
        valuesSheet.getCell(valueIndex + 3, valueColumnIndex).value = valueName;
    });

    const rangeStart = excelAddress.encodeAddress(3, inputColumnIndex);
    const rangeEnd = excelAddress.encodeAddress(9999, inputColumnIndex);

    const formulaeRangeLetter = excelAddress.encodeAddress(1, valueColumnIndex).replace("1", "");
    const dataValidation: DataValidation = {
        allowBlank: false,
        error: "Please use the drop down to select a valid value",
        errorTitle: "Invalid Selection",
        formulae: [`Values!$${formulaeRangeLetter}$3:$${formulaeRangeLetter}$${columnValues.length + 2}`],
        showErrorMessage: true,
        type: "list"
    };
    inputSheet.dataValidations.model[`${rangeStart}:${rangeEnd}`] = dataValidation;
};

function addReportingLabelsColumn(
    valuesSheet: ExcelJS.Worksheet,
    inputSheet: ExcelJS.Worksheet,
    valueColumnIndex: number,
    inputColumnIndex: number
) {
    valuesSheet.getColumn(valueColumnIndex).key = "reportingLabels";
    valuesSheet.getCell(2, valueColumnIndex).value = "reportingLabels";
    valuesSheet.getColumn(valueColumnIndex).width = 40;

    inputSheet.getColumn(inputColumnIndex).key = "reportingLabels";
    inputSheet.getCell(2, inputColumnIndex).value = "reportingLabels";
    inputSheet.getColumn(inputColumnIndex).width = 40;
}

function getArrayValue(val: ArrayEditableValue[]) {
    const selectedOption = val.find(option => option.selected) ?? { label: "" };
    return selectedOption.label;
}

function normalizeTranslationPhrase(str: string) {
    return str.toLowerCase().replace(/\W/g, "");
}

function createFile(blob: Blob, exportedFilename: string) {
    const url = URL.createObjectURL(blob);

    try {
        downloadFile(url, exportedFilename);
    } finally {
        URL.revokeObjectURL(url);
    }
}

/**
 * Restricted editables are hidden from users without the `canManageRestrictedEditables` permission, and the API
 * rejects any save that overwrites them. Exporting them would produce a spreadsheet that cannot be imported back
 * and saved, so they are left out of the export for those users.
 */
export function getExportableEditables(editables: Editable[], canSeeRestrictedEditables: boolean) {
    return editables.filter(
        editable =>
            ExportableEditableTypes.includes(editable.type) && (canSeeRestrictedEditables || !editable.restricted)
    );
}

export function getExportValueFromOverwrite({
    editable,
    masterTemplateId,
    overwrite,
    campaignMasterTemplateIds
}: {
    editable: Editable;
    masterTemplateId: string;
    overwrite: EditableOverwrite | null;
    campaignMasterTemplateIds?: string[];
}) {
    let value = editableUtils.computeEditableValue(editable, masterTemplateId, overwrite);

    const getMediaItemValue = (ov: EditableOverwrite | null) => {
        if (ov?.mediaItem) {
            return `:hoxton/${ov.mediaItem.id}/${ov.mediaItem.name}`;
        }

        return ":hoxton/template-default";
    };

    if (
        EditableType.File === editable.type ||
        EditableType.Image === editable.type ||
        EditableType.Video === editable.type
    ) {
        value = getMediaItemValue(overwrite);
    }

    if (EditableType.Array === editable.type && Array.isArray(value)) {
        return getArrayValue(value);
    }

    if (EditableType.Boolean === editable.type) {
        return (value as boolean) || value === "true" ? "true" : "false";
    }

    if (value !== null && value !== undefined && value !== "") {
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
            return String(value);
        }

        return "";
    }

    if (campaignMasterTemplateIds) {
        // lets try to work out a common default value if there is not obvious value.
        return editableUtils.getGroupPlaceholder(editable, campaignMasterTemplateIds, "") as string;
    }

    return "";
}

export default class ImportExport {
    $apollo: typeof apolloClient;
    $store: typeof store;
    excelJS: typeof import("exceljs") | null;

    constructor(apollo: typeof apolloClient, storeInstance: typeof store) {
        this.$apollo = apollo;
        this.$store = storeInstance;
        this.excelJS = null;
    }

    async loadExcelJS() {
        this.excelJS ??= await loadExcelJs();

        return this.excelJS;
    }

    async applyEditableUpdate(editableUpdate: OverwriteWithEditable) {
        const updatedOverwrite: OverwriteWithEditable | null = await this.getOverwriteUpdatedByImport(
            editableUpdate,
            editableUpdate.language,
            editableUpdate.editableGroupValueIds
        );

        if (!updatedOverwrite) {
            return;
        }

        const updatedEditableWithOverwrite = {
            editable: editableUpdate.editable,
            overwrites: [updatedOverwrite]
        };

        const updatedEditables = editableUtils.mergeEditables(this.$store.state.editor.localEditableOverwrites, [
            // this is stupid, overwrite is not OverwriteWithEditable and we shouldn't just merge them, but yolo
            updatedEditableWithOverwrite as unknown as EditableWithOverwrites
        ]);

        await this.$store.dispatch(EditorAction.SetLocalEditableOverwrites, updatedEditables);
    }

    async exportLibrarySelectTemplate() {
        await this.loadExcelJS();

        const { editableGroupIds } = this.$store.state.campaign.normalized;
        const masterTemplateLabelMap = (this.$store.getters as Record<string, unknown>)[
            CampaignGetters.masterTemplatesBySize
        ] as Record<string, string[]>;
        const masterTemplateLabels = Object.keys(masterTemplateLabelMap);
        const workbook = new this.excelJS!.Workbook();
        const hogarthLogo = workbook.addImage({
            base64: BRAND_LOGO_BASE64,
            extension: "png"
        });

        workbook.views = [
            {
                x: 0,
                y: 0,
                width: 10000,
                height: 20000,
                firstSheet: 1,
                activeTab: 1,
                visibility: "visible"
            }
        ];

        const valuesSheet = workbook.addWorksheet("Values");
        const valueFixedHeaders = ["size", "language"];
        const inputFixedHeaders = ["size", "language"];

        addInfoRow(
            valuesSheet,
            valueFixedHeaders,
            editableGroupIds.map(groupName => ({ name: groupName })),
            hogarthLogo,
            "Overviews",
            "values to be used when selecting the variation"
        );
        valuesSheet.addRow([...valueFixedHeaders, ...editableGroupIds]);
        setHeaderRowStyle(valuesSheet, 2);

        const inputSheet = workbook.addWorksheet("Input");
        addInfoRow(
            inputSheet,
            inputFixedHeaders,
            editableGroupIds.map(groupName => ({ name: groupName })),
            hogarthLogo,
            "Overview",
            "values to be used when selecting the variation"
        );
        inputSheet.addRow([...inputFixedHeaders, ...editableGroupIds]);
        setHeaderRowStyle(inputSheet, 2);

        addValuesAndFilters(masterTemplateLabels, valuesSheet, inputSheet, 1, 1);
        addValuesAndFilters(this.$store.state.campaign.languages, valuesSheet, inputSheet, 2, 2);

        editableGroupIds.forEach((groupName, paramColumnIndex) => {
            const groupValueIdsForGroup =
                this.$store.state.campaign.normalized.editableGroups[groupName]!.editableGroupValues;

            const stringValuesForGroup = groupValueIdsForGroup.map(
                groupValueId => this.$store.state.campaign.editableGroupValues[groupValueId]!.value
            );

            addValuesAndFilters(
                stringValuesForGroup,
                valuesSheet,
                inputSheet,
                paramColumnIndex + valueFixedHeaders.length + 1,
                paramColumnIndex + inputFixedHeaders.length + 1
            );
        });

        // Add reporting labels column
        const reportingLabelsColumnIndex = editableGroupIds.length + valueFixedHeaders.length + 1;
        const inputReportingLabelsColumnIndex = editableGroupIds.length + inputFixedHeaders.length + 1;

        addReportingLabelsColumn(valuesSheet, inputSheet, reportingLabelsColumnIndex, inputReportingLabelsColumnIndex);

        const exportedFilename = `${this.$store.state.campaign.name}-download-request`;
        const content = await workbook.xlsx.writeBuffer();
        const blob = new Blob([content], { type: mimetype.XSLX });

        createFile(blob, exportedFilename);
    }

    async exportXLSTranslationsTemplate(language: string, canSeeRestrictedEditables = true) {
        await this.loadExcelJS();

        const translations = this.getSimplifiedTranslationMapping(language, canSeeRestrictedEditables);
        const workbook = new this.excelJS!.Workbook();
        const hogarthLogo = workbook.addImage({
            base64: BRAND_LOGO_BASE64,
            extension: "png"
        });

        const sheet = workbook.addWorksheet("translations");
        // merge by start row, start column, end row, end column (equivalent to A1:Z1)
        sheet.columns = [
            { key: "source", width: 40 },
            { key: "language", width: 64 }
        ];
        sheet.views = [{ state: "frozen", xSplit: 1, ySplit: 2 }];
        sheet.addRow(["", "Translations"]);
        setInfoRowStyle(sheet, 1);
        sheet.mergeCells(1, 2, 1, 2);

        sheet.addImage(hogarthLogo, LOGO_POSITION);

        sheet.addRow(["source", language]);
        setHeaderRowStyle(sheet, 2);

        let isContentStyleSet = false;
        Object.keys(translations).forEach(enValue => {
            const contentRow = sheet.addRow([enValue, translations[enValue]], isContentStyleSet ? "i" : undefined);

            if (!isContentStyleSet) {
                applyStyle(contentRow, valueStyle);
                const groupValueCell = contentRow.getCell(1);
                applyStyle(groupValueCell, languageGroupNameStyle);

                isContentStyleSet = true;
            }
        });

        const exportedFilename = `${this.$store.state.campaign.name}-translations-${language}`;
        const content = await workbook.xlsx.writeBuffer();
        const blob = new Blob([content], { type: mimetype.XSLX });

        createFile(blob, exportedFilename);
    }

    async exportXLSValuesTemplate(exportType: ExportType, canSeeRestrictedEditables = true) {
        await this.loadExcelJS();

        const { editableGroups, editableGroupIds, editables, masterTemplateIds } =
            this.$store.state.campaign.normalized;
        const masterTemplateLabelMap = (this.$store.getters as Record<string, unknown>)[
            CampaignGetters.masterTemplatesBySize
        ] as Record<string, string[]>;
        const masterTemplateLabels = Object.keys(masterTemplateLabelMap);
        const workbook: ExcelJS.Workbook = new this.excelJS!.Workbook();

        const config: WorksheetConfig = {
            masterTemplateLabelMap,
            masterTemplateLabels,
            masterTemplateIds,
            exportType
        };

        const seenGroups = new Set<string>();
        editableGroupIds.forEach(groupName => {
            if (seenGroups.has(groupName)) {
                return; // Skip duplicate group
            }
            seenGroups.add(groupName);

            const editablesInGroup = getExportableEditables(
                editableGroups[groupName]!.editables.map(editableId => editables[editableId]!),
                canSeeRestrictedEditables
            );

            const editableGroupValues = editableGroups[groupName]!.editableGroupValues.map(
                editableGroupValueId => this.$store.state.campaign.editableGroupValues[editableGroupValueId]!
            );

            if (!editablesInGroup.length) {
                return;
            }

            this.createWorksheetForEditableGroup(workbook, groupName, editablesInGroup, editableGroupValues, config);
        });

        const exportedFilename = `${this.$store.state.campaign.name}-${campaignDefaultLanguage}-${exportType}`;
        const content = await workbook.xlsx.writeBuffer();
        const blob = new Blob([content], { type: mimetype.XSLX });

        createFile(blob, exportedFilename);
    }

    private createWorksheetForEditableGroup(
        workbook: ExcelJS.Workbook,
        groupName: string,
        editablesInGroup: Editable[],
        editableGroupValues: { _id: string; value: string }[],
        config: WorksheetConfig
    ) {
        const hogarthLogo = workbook.addImage({
            base64: BRAND_LOGO_BASE64,
            extension: "png"
        });

        // Deduplicate editables by ID to prevent duplicate columns
        const uniqueEditables = Array.from(new Map(editablesInGroup.map(e => [e._id, e])).values());

        const sheet = this.setupWorksheet(workbook, groupName, uniqueEditables, config.exportType, hogarthLogo);

        this.populateWorksheetData(sheet, uniqueEditables, editableGroupValues, config);
    }

    private setupWorksheet(
        workbook: ExcelJS.Workbook,
        groupName: string,
        editablesInGroup: Editable[],
        exportType: ExportType,
        hogarthLogo: number
    ): ExcelJS.Worksheet {
        const sheet = workbook.addWorksheet(groupName);

        // merge by start row, start column, end row, end column (equivalent to A1:Z1)
        sheet.columns = [
            { key: "groupValue", width: 40, style: labelStyle },
            { key: "template", width: 32 },
            ...editablesInGroup.map(({ name }) => ({
                key: name,
                width: 32
            }))
        ];

        sheet.views = [{ state: "frozen", xSplit: exportType === "global" ? 1 : 2, ySplit: 2 }];

        sheet.addRow(["", groupName]);
        setInfoRowStyle(sheet, 1);
        sheet.mergeCells(1, 2, 1, editablesInGroup.length + 1);
        sheet.getCell(1, 2).value = {
            richText: [
                {
                    font: {
                        ...silkaFont,
                        bold: true,
                        size: 20
                    },
                    text: `${this.$store.state.campaign.client.name}: ${this.$store.state.campaign.name} \r\n`
                },
                {
                    font: {
                        ...silkaFont
                    },
                    text: groupName
                }
            ]
        };

        sheet.addImage(hogarthLogo, LOGO_POSITION);
        const fixedHeaders = exportType === "global" ? ["Version Name"] : ["Version Name", "Template"];
        sheet.addRow([...fixedHeaders, ...editablesInGroup.map(({ name }) => name)]);
        setHeaderRowStyle(sheet, 2);

        return sheet;
    }

    private populateWorksheetData(
        sheet: ExcelJS.Worksheet,
        editablesInGroup: Editable[],
        editableGroupValues: { _id: string; value: string }[],
        config: WorksheetConfig
    ) {
        let isContentStyleSet = false;
        const masterTemplateLabelsToExport =
            config.exportType === "global" ? [ExportGroupScopeLabel] : config.masterTemplateLabels;

        editableGroupValues.forEach(editableGroup => {
            masterTemplateLabelsToExport.forEach(templateName => {
                // we take the value from the first master template that matches the name.
                const masterTemplateId = config.masterTemplateLabelMap[templateName]
                    ? config.masterTemplateLabelMap[templateName][0]
                    : undefined;

                const editableExportValues = editablesInGroup.map(editable =>
                    this.getExportValue(editable._id, masterTemplateId, [editableGroup._id], config.masterTemplateIds)
                );
                // we want to ignore templates column for global values export
                const fixedCells =
                    config.exportType === "global" ? [editableGroup.value] : [editableGroup.value, templateName];
                const contentRow = sheet.addRow(
                    [...fixedCells, ...editableExportValues],
                    isContentStyleSet ? "i" : undefined
                );

                if (!isContentStyleSet) {
                    applyStyle(contentRow, valueStyle);
                    const groupValueCell = contentRow.getCell(1);
                    applyStyle(groupValueCell, labelStyle);

                    isContentStyleSet = true;
                }
            });
        });
    }

    async getMediaItemById(mediaItemId: string) {
        return this.$apollo?.query<{ mediaItem: OverwriteMediaItem } | undefined>({
            query: mediaItemQuery,
            variables: {
                mediaItemId
            }
        });
    }

    async getMediaItemFromImportString(
        editableUpdate: OverwriteWithEditable
    ): Promise<{ mediaItem: OverwriteMediaItem; mediaItemId: string }> {
        const mediaItemIdRegex = /:hoxton\/([^/]*)/;

        const valueStr = Array.isArray(editableUpdate.value)
            ? JSON.stringify(editableUpdate.value)
            : String(editableUpdate.value ?? "");

        if (!editableUpdate.value || typeof editableUpdate.value !== "string") {
            throw new Error(`Invalid value: "${valueStr}" is not a valid Assemble file reference`);
        }

        const match = mediaItemIdRegex.exec(editableUpdate.value);

        if (!match?.[1]) {
            throw new Error(`"${valueStr}" is not a valid Assemble file reference format`);
        }

        const mediaItemId = match[1];

        const result = await this.getMediaItemById(mediaItemId);

        if (!result?.data?.mediaItem) {
            throw new Error(`"${valueStr}" is not a valid file reference`);
        }

        return { mediaItem: result.data.mediaItem, mediaItemId };
    }

    async getOverwriteUpdatedByImport(
        updateData: OverwriteWithEditable,
        language: string,
        editableGroupValueIds: string[]
    ) {
        const editableUpdate = deepClone(updateData);
        // Set mediaItem to null if not found for consistency

        if (editableUpdate.value === ":hoxton/template-default" || editableUpdate.value === null) {
            // default value was used go generate this value so there is no need to create any overwrites
            return null;
        }

        if (editableUpdate.mediaItem === null) {
            await this.handleMediaItemUpdate(editableUpdate);
        }

        // Unknown scope so return an empty object
        if (!Object.values(OverwriteScope).includes(editableUpdate.scope)) {
            return null;
        }
        // Set base property of overwrite
        const updatedEditable = editableUtils.setScopeValues(editableUpdate.scope, {
            language,
            scope: editableUpdate.scope,
            editableGroupValueIds,
            masterTemplateId: editableUpdate.masterTemplateId!
        }) as EditableOverwrite;
        // Set tmp overwriteId
        const { localOverwriteTmpId } = this.$store.state.editor;
        updatedEditable._id = `tmpId-${localOverwriteTmpId}`;
        await this.$store.dispatch(EditorAction.SetLocalOverwriteTmpId, localOverwriteTmpId + 1);
        // Set value properties
        updatedEditable.value = editableUpdate.value;
        updatedEditable.mediaItem = editableUpdate.mediaItem;

        if (editableUpdate.settings) {
            updatedEditable.settings = editableUpdate.settings;
        } else {
            const existingOverwrite = this.getSavedOverwrite(
                editableUpdate.editable._id,
                editableUpdate.masterTemplateId ?? null,
                language,
                editableGroupValueIds
            );
            const settingsFromScope = existingOverwrite?.settingsFromScope?.[editableUpdate.scope];
            updatedEditable.settings = settingsFromScope ?? existingOverwrite?.settings ?? undefined;
        }

        // Return updated editable
        return {
            ...updatedEditable,
            editable: editableUpdate.editable,
            editableGroupValueIds: editableUpdate.editableGroupValueIds,
            masterTemplateId: editableUpdate.masterTemplateId,
            language: editableUpdate.language
        };
    }

    private async handleMediaItemUpdate(editableUpdate: OverwriteWithEditable) {
        if (
            editableUpdate.editable.type === EditableType.File ||
            editableUpdate.editable.type === EditableType.Image ||
            editableUpdate.editable.type === EditableType.Video
        ) {
            await this.handleFileTypeMediaItem(editableUpdate);
        } else {
            editableUpdate.mediaItem = null;
        }
    }

    private async handleFileTypeMediaItem(editableUpdate: OverwriteWithEditable) {
        if (
            editableUpdate.value &&
            typeof editableUpdate.value === "string" &&
            editableUpdate.value.startsWith(":hoxton/")
        ) {
            const { mediaItem, mediaItemId } = await this.getMediaItemFromImportString(editableUpdate);
            editableUpdate.mediaItem = mediaItem;
            editableUpdate.value = mediaItemId;
        } else if (
            editableUpdate.value &&
            typeof editableUpdate.value === "string" &&
            isValidHttpUrl(editableUpdate.value)
        ) {
            throw new Error(
                `The "${editableUpdate.editable.type}" type for "${editableUpdate.editable.name}" is not supported for external feeds. You should use type: "text" instead.`
            );
        } else {
            const valueStr = Array.isArray(editableUpdate.value)
                ? JSON.stringify(editableUpdate.value)
                : String(editableUpdate.value ?? "");
            throw new Error(`"${valueStr}" is not a valid file reference`);
        }
    }

    async simplifiedLanguageUpdate({
        language,
        languageMapping,
        canSeeRestrictedEditables = true
    }: {
        language: string;
        languageMapping: Record<string, string>;
        canSeeRestrictedEditables?: boolean;
    }) {
        const { editableGroups, editableGroupIds, editables, masterTemplateIds } =
            this.$store.state.campaign.normalized;

        const normalizedLanguageMapping = Object.keys(languageMapping).reduce((acc: Record<string, string>, curr) => {
            const normalizedPhrase = normalizeTranslationPhrase(curr);
            acc[normalizedPhrase] = languageMapping[curr]!;

            return acc;
        }, {});

        const updates: OverwriteWithEditable[] = [];

        const config = {
            editableGroups,
            editables,
            masterTemplateIds,
            language,
            languageMapping,
            normalizedLanguageMapping,
            canSeeRestrictedEditables
        };

        // for each group
        editableGroupIds.forEach(editableGroupId => {
            this.processEditableGroupForLanguageUpdate(editableGroupId, updates, config);
        });

        await Promise.all(updates.map(editableUpdate => this.applyEditableUpdate(editableUpdate)));
    }

    getImportScopeAndSettings(
        overwriteTargetLang: EditableOverwrite | null,
        overwriteDefaultLang: EditableOverwrite | null,
        masterTemplateId: string | null
    ): { isLocked: boolean; scope: OverwriteScopeType; settings: CSSSettings | null } | null {
        const isTargetLocked = overwriteTargetLang?.valueFromScope === OverwriteScope.EditableGroupTemplate;
        if (isTargetLocked) {
            return null;
        }

        let scope: OverwriteScopeType = OverwriteScope.EditableGroup;

        const isDefaultTemplateScope = overwriteDefaultLang?.scope === OverwriteScope.EditableGroupTemplate;

        if (isDefaultTemplateScope && masterTemplateId) {
            scope = overwriteDefaultLang.scope;
        }

        const settingsToUse = overwriteTargetLang?.settings ?? overwriteDefaultLang?.settingsFromScope?.[scope] ?? null;

        return { isLocked: false, scope, settings: settingsToUse };
    }

    private processEditableGroupForLanguageUpdate(
        editableGroupId: string,
        updates: OverwriteWithEditable[],
        config: LanguageUpdateConfig
    ) {
        const editableGroup = config.editableGroups[editableGroupId];
        const importableEditables = editableGroup!.editables.filter(
            (editableId: string) =>
                ExportableTranslationTypes.includes(config.editables[editableId]!.type) &&
                (config.canSeeRestrictedEditables || !config.editables[editableId]!.restricted)
        );

        if (!importableEditables.length) {
            return;
        }

        // these are the editables that we should check values for.
        importableEditables.forEach((editableId: string) => {
            // passing all possible groups values in a loop
            editableGroup!.editableGroupValues.forEach((egvId: string) => {
                // iterate over group as well as all template level overwrites
                ["", ...config.masterTemplateIds].forEach(masterTemplateId => {
                    const overwriteDefaultLang = this.getSavedOverwrite(
                        editableId,
                        masterTemplateId,
                        campaignDefaultLanguage,
                        [egvId]
                    );

                    const defaultLangVal =
                        (editableUtils.computeEditableValue(
                            config.editables[editableId]!,
                            masterTemplateId,
                            overwriteDefaultLang
                        ) as string | null) ?? "";

                    if (config.languageMapping[defaultLangVal]) {
                        // update value when we get an exact values match
                        const overwriteTargetLang = this.getSavedOverwrite(
                            editableId,
                            masterTemplateId,
                            config.language,
                            [egvId]
                        );

                        const result = this.getImportScopeAndSettings(
                            overwriteTargetLang,
                            overwriteDefaultLang,
                            masterTemplateId
                        );

                        if (!result) {
                            return;
                        }

                        updates.push({
                            scope: result.scope,
                            language: config.language,
                            value: config.languageMapping[defaultLangVal],
                            editable: config.editables[editableId]!,
                            editableGroupValueIds: [egvId],
                            mediaItem: null,
                            settings: result.settings,
                            ...(result.scope === OverwriteScope.EditableGroupTemplate && { masterTemplateId })
                        });

                        return;
                    }

                    const normalisedVal = normalizeTranslationPhrase(defaultLangVal);
                    if (config.normalizedLanguageMapping[normalisedVal]) {
                        // if we don't get exact match normalize the string and try again
                        const overwriteTargetLang = this.getSavedOverwrite(
                            editableId,
                            masterTemplateId,
                            config.language,
                            [egvId]
                        );

                        const result = this.getImportScopeAndSettings(
                            overwriteTargetLang,
                            overwriteDefaultLang,
                            masterTemplateId
                        );

                        if (!result) {
                            return;
                        }

                        updates.push({
                            scope: result.scope,
                            language: config.language,
                            value: config.normalizedLanguageMapping[normalisedVal],
                            editable: config.editables[editableId]!,
                            editableGroupValueIds: [egvId],
                            mediaItem: null,
                            settings: result.settings,
                            ...(result.scope === OverwriteScope.EditableGroupTemplate && { masterTemplateId })
                        });
                    }
                });
            });
        });
    }

    getExportValue(
        editableId: string,
        masterTemplateId = "",
        groupValueIds: string[] = [],
        campaignMasterTemplateIds: string[] = []
    ) {
        const { editables } = this.$store.state.campaign.normalized;

        const savedOverwrite = this.getSavedOverwrite(
            editableId,
            masterTemplateId,
            campaignDefaultLanguage,
            groupValueIds
        );

        return getExportValueFromOverwrite({
            editable: editables[editableId]!,
            masterTemplateId,
            overwrite: savedOverwrite,
            campaignMasterTemplateIds
        });
    }

    getGroupValueId(groupName: string, groupValueName: string) {
        const groupValueNamesByGroup = (this.$store.getters as Record<string, unknown>)[
            CampaignGetters.groupValueNamesByGroup
        ] as Record<string, { editableGroupValuesNamesMap: Record<string, string> }>;

        return groupValueNamesByGroup[groupName]!.editableGroupValuesNamesMap[groupValueName];
    }

    getSavedOverwrite(editableId: string, templateId: string | null, language: string, selectedGroupsIds: string[]) {
        const editorOverwrites = (this.$store.getters as Record<string, unknown>)[
            EditorGetters.editableOverwrites
        ] as EditableWithOverwrites[];

        const getterFunction = (this.$store.getters as Record<string, unknown>)[
            CampaignGetters.editableOverwriteValue
        ] as (
            editableId: string,
            templateId: string | null,
            language: string,
            selectedGroupsIds: string[],
            overwrites?: EditableWithOverwrites[]
        ) => EditableOverwrite | null;

        return getterFunction(editableId, templateId, language, selectedGroupsIds, editorOverwrites);
    }

    getTranslationValue(editableId: string, language: string, masterTemplateId = "", groupValueIds: string[] = []) {
        const { editables } = this.$store.state.campaign.normalized;

        const overwriteDefaultLang = this.getSavedOverwrite(
            editableId,
            masterTemplateId,
            campaignDefaultLanguage,
            groupValueIds
        );

        const overwriteLang = this.getSavedOverwrite(editableId, masterTemplateId, language, groupValueIds);

        const cmpDefaultLangValue =
            (editableUtils.computeEditableValue(editables[editableId]!, masterTemplateId, overwriteDefaultLang) as
                | string
                | null) ?? "";

        let languageValue = "";

        if (language === campaignDefaultLanguage || overwriteDefaultLang?._id !== overwriteLang?._id) {
            languageValue =
                (editableUtils.computeEditableValue(editables[editableId]!, masterTemplateId, overwriteLang) as
                    | string
                    | null) ?? "";
        }

        return {
            [campaignDefaultLanguage]: cmpDefaultLangValue,
            [language]: languageValue
        };
    }

    getSimplifiedTranslationMapping(language = "en", canSeeRestrictedEditables = true) {
        const translations: Record<string, string> = {};
        const { editableGroups, editableGroupIds, masterTemplateIds, editables } =
            this.$store.state.campaign.normalized;

        editableGroupIds.forEach(editableGroupId => {
            this.processEditableGroupForTranslations(
                editableGroupId,
                editableGroups,
                editables,
                masterTemplateIds,
                language,
                translations,
                canSeeRestrictedEditables
            );
        });

        return translations;
    }

    private processEditableGroupForTranslations(
        editableGroupId: string,
        editableGroups: Record<string, { editables: string[]; editableGroupValues: string[] }>,
        editables: Record<string, Editable>,
        masterTemplateIds: string[],
        language: string,
        translations: Record<string, string>,
        canSeeRestrictedEditables: boolean
    ) {
        const editableGroup = editableGroups[editableGroupId];
        const exportable = editableGroup!.editables.filter(
            editableId =>
                ExportableTranslationTypes.includes(editables[editableId]!.type) &&
                (canSeeRestrictedEditables || !editables[editableId]!.restricted)
        );

        if (!exportable.length) {
            return;
        }

        // there are editables that we should check values for.
        editableGroup!.editableGroupValues.forEach(egvId => {
            this.processEditableTranslations(exportable, egvId, masterTemplateIds, language, translations);
        });
    }

    private processEditableTranslations(
        exportableEditables: string[],
        egvId: string,
        masterTemplateIds: string[],
        language: string,
        translations: Record<string, string>
    ) {
        exportableEditables.forEach(editableId => {
            // iterate over group as well as all template level overwrites
            ["", ...masterTemplateIds].forEach(masterTemplateId => {
                const translationObject = this.getTranslationValue(editableId, language, masterTemplateId, [egvId]);

                // we don't want empty entries and values that look like url
                if (
                    translationObject[campaignDefaultLanguage] &&
                    translationObject[language] &&
                    !urlRegex.test(translationObject[campaignDefaultLanguage])
                ) {
                    translations[translationObject[campaignDefaultLanguage]] = translationObject[language];
                }
            });
        });
    }

    parseGroupValueImportRow(
        row: (string | null)[],
        groupName: string,
        lastRelevantColumn: number,
        allSizesImport: boolean
    ) {
        let groupValueName: string | null | undefined;
        let masterTemplateName: string | null | undefined;
        let allEditableValues: (string | null)[];

        if (allSizesImport) {
            [groupValueName, ...allEditableValues] = row;
            masterTemplateName = ExportGroupScopeLabel;
        } else {
            [groupValueName, masterTemplateName, ...allEditableValues] = row;
        }

        if (!groupValueName) {
            return null;
        }

        const editableValues = allEditableValues.slice(0, lastRelevantColumn);

        return {
            groupValueName,
            masterTemplateName,
            editableValues,
            groupValueId: this.getGroupValueId(groupName, groupValueName)
        };
    }
}
